import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateProfile, changeEmail, changePassword, updateBusinessCategories, updateBusinessServiceCities, updateBusinessLocation, deleteAccount, getSubscriptionStatus, getBadge } from '../services/api';
import { CATEGORY_OPTIONS, formatCategory } from '../constants/categories';
import { BATON_ROUGE_METRO } from '../constants/metros';
import { TIER_LIMITS, isTierId, TierId } from '../constants/tierLimits';
import { classifyFootprintError, laneReasonText, failingPairLabel, FailingPair } from '../utils/footprintErrors';
import BadgeDisplay, { BadgeData, disputeSummary } from './ReputationComponents';

const CollapseSection: React.FC<{ title: string; icon: string; danger?: boolean; children: React.ReactNode }> = ({ title, icon, danger, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={danger ? styles.dangerSection : styles.section}>
      <div style={styles.collapseHeader} onClick={() => setOpen(!open)}>
        <h3 style={danger ? styles.dangerTitle : styles.sectionTitle}>{icon} {title}</h3>
        <span style={styles.arrow}>{open ? '\u25B2' : '\u25BC'}</span>
      </div>
      {open && <div style={styles.collapseBody}>{children}</div>}
    </div>
  );
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);

  // Read-only "Your Trust Status" data. Business/contractor users only; the
  // fetch is skipped entirely for customers (same gating Dashboard uses).
  const [badge, setBadge] = useState<BadgeData | null>(null);
  const [badgeLoading, setBadgeLoading] = useState(true);
  const [badgeError, setBadgeError] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  // Plan tier MUST come from the backend (GET /subscription/status) - never
  // localStorage. localStorage profile is the source only for the initially
  // selected chips.
  const [planTier, setPlanTier] = useState<TierId | null>(null);
  // BLEEDING-STOP1: has GET /subscription/status answered yet (either way)?
  // Gates both footprint save buttons. planLoadFailed records that it answered
  // with an error, so the re-enable can be explained rather than silent.
  const [planLoaded, setPlanLoaded] = useState(false);
  const [planLoadFailed, setPlanLoadFailed] = useState(false);

  const [categories, setCategories] = useState<string[]>([]);
  const [catMsg, setCatMsg] = useState('');
  const [catErr, setCatErr] = useState('');
  const [catLoading, setCatLoading] = useState(false);
  // Grandfather ceiling for categories: the selected count at load time (and
  // after each successful save). effectiveLimit = max(planLimit, ceiling).
  const [catCeiling, setCatCeiling] = useState(0);
  const [catEditsRemaining, setCatEditsRemaining] = useState<number | null>(null);
  const [catPlanLimitMsg, setCatPlanLimitMsg] = useState<string | null>(null);
  const [catLanePairs, setCatLanePairs] = useState<FailingPair[] | null>(null);

  const [serviceCities, setServiceCities] = useState<string[]>([]);
  const [svcCitiesMsg, setSvcCitiesMsg] = useState<string | null>(null);
  const [svcCitiesErr, setSvcCitiesErr] = useState<string | null>(null);
  const [svcCitiesLoading, setSvcCitiesLoading] = useState(false);
  const [cityCeiling, setCityCeiling] = useState(0);
  const [cityEditsRemaining, setCityEditsRemaining] = useState<number | null>(null);
  const [cityPlanLimitMsg, setCityPlanLimitMsg] = useState<string | null>(null);
  const [cityLanePairs, setCityLanePairs] = useState<FailingPair[] | null>(null);

  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [bizState, setBizState] = useState('');
  const [zip, setZip] = useState('');
  const [locMsg, setLocMsg] = useState('');
  const [locErr, setLocErr] = useState('');
  const [locLoading, setLocLoading] = useState(false);

  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');
  const [deleteErr, setDeleteErr] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const u = JSON.parse(stored);
    setUser(u);
    setName(u.name || '');
    setPhone(u.phone || '');
    const initialCats: string[] = u.categories || [];
    const initialCities: string[] = u.service_cities || [];
    setCategories(initialCats);
    setServiceCities(initialCities);
    // Capture the grandfather ceiling from the current footprint at load.
    setCatCeiling(initialCats.length);
    setCityCeiling(initialCities.length);
  }, [navigate]);

  // Source the plan tier from the backend, not localStorage. Footprint limits
  // are derived from this; until it resolves, limits fall back to swap-only.
  //
  // BLEEDING-STOP1: the tier lives at res.data.subscription.tier, NOT
  // res.data.tier. GET /subscription/status responds
  // { subscription: {...}, quotes: {...} } (backend subscriptionRoutes.ts:32)
  // and api.ts returns the raw axios response with no unwrapping interceptor.
  // Reading the wrong path made isTierId() always false, so setPlanTier never
  // fired, planCategoryLimit computed to 0, and every unselected chip rendered
  // permanently disabled - no contractor could add a category or city.
  //
  // planLoaded gates BOTH save buttons: until the server has answered, the
  // page has no server-confirmed view and must not fire a full-array replace.
  // It is set in the .catch too - a failed fetch must not disable the buttons
  // forever - but planLoadFailed then surfaces a visible notice so the
  // re-enable is never silent.
  useEffect(() => {
    if (!user || user.user_type !== 'business') return;
    let cancelled = false;
    getSubscriptionStatus()
      .then(res => {
        if (cancelled) return;
        if (isTierId(res.data?.subscription?.tier)) setPlanTier(res.data.subscription.tier);
        setPlanLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        // planTier stays null; limits degrade to swap-only. Unblock the
        // buttons but tell the user the plan check did not complete.
        setPlanLoadFailed(true);
        setPlanLoaded(true);
      });
    return () => { cancelled = true; };
  }, [user]);

  // Read-only trust status. Same call signature Dashboard uses
  // (getBadge(currentUserId)). Skip the fetch for customers / missing id.
  useEffect(() => {
    if (!user || user.user_type !== 'business' || !user.id) {
      setBadge(null);
      setBadgeError(false);
      setBadgeLoading(false);
      return;
    }
    let cancelled = false;
    getBadge(user.id)
      .then(res => { if (!cancelled) setBadge(res.data); })
      .catch(() => { if (!cancelled) setBadgeError(true); })
      .finally(() => { if (!cancelled) setBadgeLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  // Effective limits: max(plan limit, grandfather ceiling), independently per
  // dimension. While planTier is unresolved, planLimit is treated as 0 so the
  // user can swap within their current footprint but not grow until it loads.
  const planCategoryLimit = planTier ? TIER_LIMITS[planTier].maxCategories : 0;
  const planCityLimit = planTier ? TIER_LIMITS[planTier].maxCities : 0;
  const effectiveCategoryLimit = Math.max(planCategoryLimit, catCeiling);
  const effectiveCityLimit = Math.max(planCityLimit, cityCeiling);
  const categoriesGrandfathered = planTier !== null && catCeiling > planCategoryLimit;
  const citiesGrandfathered = planTier !== null && cityCeiling > planCityLimit;

  const handleProfileSave = async () => {
    setProfileLoading(true); setProfileMsg(''); setProfileErr('');
    try {
      const res = await updateProfile({ name, phone });
      const updated = { ...user, name: res.data.user.name, phone: res.data.user.phone };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
      setProfileMsg('Profile updated!');
    } catch (err: any) { setProfileErr(err.response?.data?.error || 'Failed to update'); }
    finally { setProfileLoading(false); }
  };

  const handleEmailChange = async () => {
    setEmailLoading(true); setEmailMsg(''); setEmailErr('');
    try {
      const res = await changeEmail({ newEmail, password: emailPassword });
      setEmailMsg(res.data.message); setNewEmail(''); setEmailPassword('');
      if (res.data.requiresLogout) {
        setTimeout(() => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); }, 3000);
      }
    } catch (err: any) { setEmailErr(err.response?.data?.error || 'Failed to change email'); }
    finally { setEmailLoading(false); }
  };

  const handlePasswordChange = async () => {
    setPwLoading(true); setPwMsg(''); setPwErr('');
    if (newPw !== confirmPw) { setPwErr('New passwords do not match'); setPwLoading(false); return; }
    if (newPw.length < 8) { setPwErr('Password must be at least 8 characters'); setPwLoading(false); return; }
    try {
      const res = await changePassword({ currentPassword: currentPw, newPassword: newPw });
      setPwMsg(res.data.message); setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) { setPwErr(err.response?.data?.error || 'Failed to change password'); }
    finally { setPwLoading(false); }
  };

  const toggleCategory = (cat: string) => {
    // F4-a effectiveLimit supersedes CATCITY1's flat MAX_CATEGORIES_PER_BUSINESS
    // (15) guard: effectiveLimit = max(planLimit, grandfatherCeiling) and the
    // backend absolute ceiling is 15, so this is always <= 15 — the flat guard
    // is redundant once this is in. Do not stack both.
    setCategories(prev => {
      if (prev.includes(cat)) return prev.filter(c => c !== cat); // always removable
      if (prev.length >= effectiveCategoryLimit) return prev;     // cannot grow past the ceiling
      return [...prev, cat];
    });
  };

  const handleCategorySave = async () => {
    setCatLoading(true); setCatMsg(''); setCatErr(''); setCatPlanLimitMsg(null); setCatLanePairs(null);
    if (categories.length === 0) { setCatErr('Please select at least one category'); setCatLoading(false); return; }
    try {
      const res = await updateBusinessCategories({ categories });
      const updated = { ...user, categories };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
      // Successful save reflows the grandfather ceiling to the saved count.
      setCatCeiling(categories.length);
      if (typeof res.data.editsRemaining === 'number') setCatEditsRemaining(res.data.editsRemaining);
      setCatMsg(res.data.warning || res.data.message);
    } catch (err: any) {
      const footprint = classifyFootprintError(err);
      if (footprint?.kind === 'plan-limit') setCatPlanLimitMsg(footprint.message);
      else if (footprint?.kind === 'lanes') setCatLanePairs(footprint.pairs);
      else setCatErr(err.response?.data?.error || 'Failed to update categories');
    }
    finally { setCatLoading(false); }
  };

  const toggleServiceCity = (city: string) => {
    setServiceCities(prev => {
      if (prev.includes(city)) return prev.filter(c => c !== city); // always removable
      if (prev.length >= effectiveCityLimit) return prev;           // cannot grow past the ceiling
      return [...prev, city];
    });
  };

  const handleServiceCitiesSave = async () => {
    setSvcCitiesLoading(true); setSvcCitiesMsg(null); setSvcCitiesErr(null); setCityPlanLimitMsg(null); setCityLanePairs(null);
    if (serviceCities.length === 0) { setSvcCitiesErr('Please select at least one city'); setSvcCitiesLoading(false); return; }
    try {
      const res = await updateBusinessServiceCities({ service_cities: serviceCities });
      const updated = { ...user, service_cities: serviceCities };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
      setCityCeiling(serviceCities.length);
      if (typeof res.data.editsRemaining === 'number') setCityEditsRemaining(res.data.editsRemaining);
      setSvcCitiesMsg(res.data.warning || res.data.message);
    } catch (err: any) {
      const footprint = classifyFootprintError(err);
      if (footprint?.kind === 'plan-limit') setCityPlanLimitMsg(footprint.message);
      else if (footprint?.kind === 'lanes') setCityLanePairs(footprint.pairs);
      else setSvcCitiesErr(err.response?.data?.error || 'Failed to update service cities');
    }
    finally { setSvcCitiesLoading(false); }
  };

  // SETTINGS-LOCATION-EMPTY-STRING-GUARD1: build the payload from only the
  // fields the contractor actually typed.
  //
  // address / city / bizState / zip are initialised to '' and never hydrated,
  // so sending all four unconditionally pushed '' for every field left
  // untouched. The backend guards with COALESCE($1, address)
  // (auth.ts:1430), which stops NULL but NOT '' — an empty string is a
  // value, so it overwrites. A contractor who typed only a new ZIP therefore
  // blanked their stored address, city and state.
  //
  // Blank fields are OMITTED from the object entirely — never null, never an
  // explicit undefined key. JSON.stringify drops absent keys, so req.body has
  // no such property, and node-postgres maps undefined to NULL
  // ("null and undefined are both null for postgres", pg utils.js:50-53).
  // COALESCE(NULL, address) then preserves the stored value.
  const handleLocationSave = async () => {
    const trimmedAddress = address.trim();
    const trimmedCity = city.trim();
    const trimmedState = bizState.trim();
    const trimmedZip = zip.trim();

    const payload: { address?: string; city?: string; state?: string; zip?: string } = {};
    if (trimmedAddress) payload.address = trimmedAddress;
    if (trimmedCity) payload.city = trimmedCity;
    if (trimmedState) payload.state = trimmedState;
    if (trimmedZip) payload.zip = trimmedZip;

    // Nothing typed (or whitespace only): send no request at all. Returns
    // before locLoading is ever set, so the button cannot hang on "Saving...".
    if (Object.keys(payload).length === 0) {
      setLocMsg('');
      setLocErr('Enter at least one location field to update.');
      return;
    }

    setLocLoading(true); setLocMsg(''); setLocErr('');
    try {
      await updateBusinessLocation(payload);
      setLocMsg('Location updated!');
    } catch (err: any) { setLocErr(err.response?.data?.error || 'Failed to update location'); }
    finally { setLocLoading(false); }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true); setDeleteMsg(''); setDeleteErr('');
    try {
      await deleteAccount(deletePassword);
      setDeleteMsg('Account deleted. Redirecting...');
      setTimeout(() => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/'); }, 2000);
    } catch (err: any) { setDeleteErr(err.response?.data?.error || 'Failed to delete account'); }
    finally { setDeleteLoading(false); }
  };

  if (!user) return null;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>{'\u2699\uFE0F'} Account Settings</h1>

      <CollapseSection title="Personal Information" icon={'\uD83D\uDC64'}>
        {profileMsg && <p style={styles.success}>{profileMsg}</p>}
        {profileErr && <p style={styles.error}>{profileErr}</p>}
        <div style={styles.field}>
          <label style={styles.label}>Name</label>
          <input style={styles.input} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Phone</label>
          <input style={styles.input} value={phone} onChange={e => setPhone(e.target.value)} />
        </div>
        <button style={styles.button} onClick={handleProfileSave} disabled={profileLoading}>
          {profileLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </CollapseSection>

      {user.user_type === 'business' && (
        <CollapseSection title="Your Trust Status" icon={'\uD83D\uDEE1\uFE0F'}>
          <p style={styles.hint}>
            Your reputation status is earned from homeowner ratings on completed jobs. This view is read-only.
          </p>
          {badgeLoading ? (
            <p style={styles.trustLoadingHint}>Loading reputation status{'\u2026'}</p>
          ) : badgeError || !badge ? (
            <p style={styles.trustLoadingHint}>Reputation status is temporarily unavailable.</p>
          ) : (
            <>
              {badge.badge_level === 'none' || badge.rating_count === 0 ? (
                <div style={styles.trustEmptyBlock}>
                  <div style={styles.trustEmptyHeading}>Build your reputation</div>
                  <div style={styles.trustEmptySub}>
                    Complete jobs and earn ratings from homeowners to unlock your QuoteBid trust badge.
                  </div>
                </div>
              ) : (
                <div style={styles.trustHeaderBlock}>
                  <BadgeDisplay
                    badgeLevel={badge.badge_level}
                    ratingAvg={badge.rating_avg}
                    ratingCount={badge.rating_count}
                    size="lg"
                  />
                </div>
              )}

              <div style={styles.trustStats}>
                <div style={styles.trustStatItem}>
                  <div style={styles.trustStatValue}>{badge.jobs_completed}</div>
                  <div style={styles.trustStatLabel}>Jobs Completed</div>
                </div>
                <div style={styles.trustStatItem}>
                  <div style={styles.trustStatValue}>
                    {badge.rating_count === 0 ? '\u2014' : `${Number(badge.rating_avg).toFixed(1)}\u2605`}
                  </div>
                  <div style={styles.trustStatLabel}>
                    {badge.rating_count === 0
                      ? 'No ratings yet'
                      : `Rated by homeowners (${badge.rating_count} review${badge.rating_count === 1 ? '' : 's'})`}
                  </div>
                </div>
                <div style={styles.trustStatItem}>
                  <div style={styles.trustStatValueSm}>
                    {new Date(badge.member_since).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                  </div>
                  <div style={styles.trustStatLabel}>Member Since</div>
                </div>
              </div>

              {(() => {
                const summary = disputeSummary(badge);
                if (!summary) return null;
                return <p style={styles.trustDisputeLine}>{summary}</p>;
              })()}
            </>
          )}
        </CollapseSection>
      )}

      {user.user_type === 'business' && (
        <CollapseSection title="Business Location" icon={'\uD83D\uDCCD'}>
          <p style={styles.hint}>Your location helps the AI match you with nearby customers.</p>
          {locMsg && <p style={styles.success}>{locMsg}</p>}
          {locErr && <p style={styles.error}>{locErr}</p>}
          <div style={styles.field}>
            <label style={styles.label}>Address</label>
            <input style={styles.input} value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St" />
          </div>
          <div style={styles.row}>
            <div style={styles.fieldHalf}>
              <label style={styles.label}>City</label>
              <input style={styles.input} value={city} onChange={e => setCity(e.target.value)} placeholder="Baton Rouge" />
            </div>
            <div style={styles.fieldQuarter}>
              <label style={styles.label}>State</label>
              <input style={styles.input} value={bizState} onChange={e => setBizState(e.target.value)} placeholder="LA" maxLength={2} />
            </div>
            <div style={styles.fieldQuarter}>
              <label style={styles.label}>ZIP</label>
              <input style={styles.input} value={zip} onChange={e => setZip(e.target.value)} placeholder="70726" maxLength={5} />
            </div>
          </div>
          <button style={styles.button} onClick={handleLocationSave} disabled={locLoading}>
            {locLoading ? 'Saving...' : 'Update Location'}
          </button>
        </CollapseSection>
      )}

      <CollapseSection title="Change Email" icon={'\uD83D\uDCE7'}>
        <p style={styles.hint}>Current email: <strong>{user.email}</strong></p>
        <p style={styles.hint}>Changing your email requires re-verification. You will be logged out.</p>
        {emailMsg && <p style={styles.success}>{emailMsg}</p>}
        {emailErr && <p style={styles.error}>{emailErr}</p>}
        <div style={styles.field}>
          <label style={styles.label}>New Email</label>
          <input style={styles.input} type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Current Password (to confirm)</label>
          <input style={styles.input} type="password" value={emailPassword} onChange={e => setEmailPassword(e.target.value)} />
        </div>
        <button style={styles.buttonOrange} onClick={handleEmailChange} disabled={emailLoading || !newEmail || !emailPassword}>
          {emailLoading ? 'Changing...' : 'Change Email'}
        </button>
      </CollapseSection>

      <CollapseSection title="Change Password" icon={'\uD83D\uDD12'}>
        {pwMsg && <p style={styles.success}>{pwMsg}</p>}
        {pwErr && <p style={styles.error}>{pwErr}</p>}
        <div style={styles.field}>
          <label style={styles.label}>Current Password</label>
          <input style={styles.input} type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>New Password</label>
          <input style={styles.input} type="password" value={newPw} onChange={e => setNewPw(e.target.value)} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Confirm New Password</label>
          <input style={styles.input} type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
        </div>
        <button style={styles.button} onClick={handlePasswordChange} disabled={pwLoading || !currentPw || !newPw || !confirmPw}>
          {pwLoading ? 'Changing...' : 'Change Password'}
        </button>
      </CollapseSection>

      {user.user_type === 'business' && (
        <CollapseSection title="Business Categories" icon={'\uD83C\uDFF7\uFE0F'}>
          {categoriesGrandfathered ? (
            <p style={styles.grandfatherHint}>
              Your plan includes up to {planCategoryLimit} categories {'\u2014'} you currently have {catCeiling} grandfathered.
              You can swap categories, but you cannot add beyond {catCeiling}.
            </p>
          ) : (
            <p style={styles.hint}>
              Select the services your business provides.
              {planTier !== null && ` Your plan includes up to ${planCategoryLimit} categories.`}
            </p>
          )}
          <p style={styles.hint}>
            {catEditsRemaining !== null
              ? `You have ${catEditsRemaining} ${catEditsRemaining === 1 ? 'edit' : 'edits'} remaining.`
              : 'You can edit this 2 times total.'}
            {' '}({categories.length} / {effectiveCategoryLimit} selected)
          </p>
          {catMsg && <p style={styles.success}>{catMsg}</p>}
          {catErr && <p style={styles.error}>{catErr}</p>}
          {catPlanLimitMsg && <p style={styles.planLimitNotice}>{catPlanLimitMsg}</p>}
          {catLanePairs && catLanePairs.length > 0 && (
            <div style={styles.laneNotice}>
              <div style={styles.laneNoticeTitle}>Some of your lanes aren't available:</div>
              <ul style={styles.laneList}>
                {catLanePairs.map((p, i) => (
                  <li key={i} style={styles.laneItem}><strong>{failingPairLabel(p, formatCategory)}</strong> {'\u2014'} {laneReasonText(p.reason)}</li>
                ))}
              </ul>
            </div>
          )}
          <p style={styles.staleWarning}>
            These show your categories from your last sign-in. If QuoteBid updated
            your account recently, log out and back in before saving {'\u2014'} saving
            now could remove those changes.
          </p>
          {planLoadFailed && (
            <p style={styles.planCheckWarning}>
              We couldn{'\u2019'}t confirm your plan limits just now. You can still
              save, but please double-check your selections first.
            </p>
          )}
          <div style={styles.categoryGrid}>
            {CATEGORY_OPTIONS.map(cat => {
              const isSelected = categories.includes(cat);
              const isDisabled = !isSelected && categories.length >= effectiveCategoryLimit;
              return (
                <div key={cat} onClick={() => toggleCategory(cat)}
                  style={{
                    ...styles.categoryChip,
                    backgroundColor: isSelected ? '#3498db' : (isDisabled ? '#e5e7eb' : '#f0f0f0'),
                    color: isSelected ? 'white' : (isDisabled ? '#9aa5b1' : '#333'),
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.55 : 1,
                  }}>
                  {formatCategory(cat)}
                </div>
              );
            })}
          </div>
          <button style={styles.buttonOrange} onClick={handleCategorySave} disabled={catLoading || !planLoaded}>
            {catLoading ? 'Saving...' : (!planLoaded ? 'Checking your plan...' : 'Update Categories')}
          </button>
        </CollapseSection>
      )}

      {user.user_type === 'business' && (
        <CollapseSection title="Service Cities" icon={'\uD83D\uDCCD'}>
          {citiesGrandfathered ? (
            <p style={styles.grandfatherHint}>
              Your plan includes up to {planCityLimit} cities {'\u2014'} you currently have {cityCeiling} grandfathered.
              You can swap cities, but you cannot add beyond {cityCeiling}.
            </p>
          ) : (
            <p style={styles.hint}>
              Select the cities you serve.
              {planTier !== null && ` Your plan includes up to ${planCityLimit} cities.`}
            </p>
          )}
          <p style={styles.hint}>
            {cityEditsRemaining !== null
              ? `You have ${cityEditsRemaining} ${cityEditsRemaining === 1 ? 'edit' : 'edits'} remaining.`
              : 'You can edit this 2 times total.'}
            {' '}({serviceCities.length} / {effectiveCityLimit} selected)
          </p>
          {svcCitiesMsg && <p style={styles.success}>{svcCitiesMsg}</p>}
          {svcCitiesErr && <p style={styles.error}>{svcCitiesErr}</p>}
          {cityPlanLimitMsg && <p style={styles.planLimitNotice}>{cityPlanLimitMsg}</p>}
          {cityLanePairs && cityLanePairs.length > 0 && (
            <div style={styles.laneNotice}>
              <div style={styles.laneNoticeTitle}>Some of your lanes aren't available:</div>
              <ul style={styles.laneList}>
                {cityLanePairs.map((p, i) => (
                  <li key={i} style={styles.laneItem}><strong>{failingPairLabel(p, formatCategory)}</strong> {'\u2014'} {laneReasonText(p.reason)}</li>
                ))}
              </ul>
            </div>
          )}
          <p style={styles.staleWarning}>
            These show your categories from your last sign-in. If QuoteBid updated
            your account recently, log out and back in before saving {'\u2014'} saving
            now could remove those changes.
          </p>
          {planLoadFailed && (
            <p style={styles.planCheckWarning}>
              We couldn{'\u2019'}t confirm your plan limits just now. You can still
              save, but please double-check your selections first.
            </p>
          )}
          <div style={styles.categoryGrid}>
            {BATON_ROUGE_METRO.map(city => {
              const isSelected = serviceCities.includes(city);
              const isDisabled = !isSelected && serviceCities.length >= effectiveCityLimit;
              return (
                <div key={city} onClick={() => toggleServiceCity(city)}
                  style={{
                    ...styles.categoryChip,
                    backgroundColor: isSelected ? '#3498db' : (isDisabled ? '#e5e7eb' : '#f0f0f0'),
                    color: isSelected ? 'white' : (isDisabled ? '#9aa5b1' : '#333'),
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.55 : 1,
                  }}>
                  {city}
                </div>
              );
            })}
          </div>
          <button style={styles.buttonOrange} onClick={handleServiceCitiesSave} disabled={svcCitiesLoading || !planLoaded}>
            {svcCitiesLoading ? 'Saving...' : (!planLoaded ? 'Checking your plan...' : 'Update Service Cities')}
          </button>
        </CollapseSection>
      )}

      <CollapseSection title="Delete Account" icon={'\u26A0\uFE0F'} danger>
        <p style={styles.hint}>This action is <strong>permanent</strong> and cannot be undone. All your data, requests, and quotes will be deleted.</p>
        {deleteMsg && <p style={styles.success}>{deleteMsg}</p>}
        {deleteErr && <p style={styles.error}>{deleteErr}</p>}
        {!deleteConfirm ? (
          <button style={styles.buttonDanger} onClick={() => setDeleteConfirm(true)}>
            I want to delete my account
          </button>
        ) : (
          <div>
            <div style={styles.field}>
              <label style={styles.label}>Enter your password to confirm</label>
              <input style={styles.input} type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Your password" />
            </div>
            <div style={styles.deleteButtons}>
              <button style={styles.buttonDangerFull} onClick={handleDeleteAccount} disabled={deleteLoading || !deletePassword}>
                {deleteLoading ? 'Deleting...' : 'Permanently Delete My Account'}
              </button>
              <button style={styles.buttonCancel} onClick={() => { setDeleteConfirm(false); setDeletePassword(''); setDeleteErr(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </CollapseSection>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '600px', margin: '0 auto', padding: '30px 20px' },
  title: { fontSize: '28px', marginBottom: '30px', color: '#2c3e50' },
  section: {
    backgroundColor: 'white', borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '12px', overflow: 'hidden',
  },
  collapseHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 25px', cursor: 'pointer', userSelect: 'none' as const,
  },
  collapseBody: { padding: '0 25px 25px' },
  arrow: { fontSize: '14px', color: '#7f8c8d' },
  sectionTitle: {
    fontSize: '16px', color: '#2c3e50', margin: '0',
  },
  field: { marginBottom: '15px' },
  fieldHalf: { flex: '2', marginBottom: '15px' },
  fieldQuarter: { flex: '1', marginBottom: '15px' },
  row: { display: 'flex', gap: '10px' },
  label: { display: 'block', marginBottom: '5px', fontWeight: '600' as const, fontSize: '14px', color: '#555' },
  input: { width: '100%', padding: '12px', fontSize: '16px', border: '1px solid #ddd', borderRadius: '5px', boxSizing: 'border-box' as const },
  button: { padding: '12px 30px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '5px', fontSize: '15px', cursor: 'pointer', fontWeight: '600' as const },
  buttonOrange: { padding: '12px 30px', backgroundColor: '#e67e22', color: 'white', border: 'none', borderRadius: '5px', fontSize: '15px', cursor: 'pointer', fontWeight: '600' as const },
  hint: { fontSize: '13px', color: '#7f8c8d', marginBottom: '15px', marginTop: '0' },
  grandfatherHint: { fontSize: '13px', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', marginTop: '0', lineHeight: 1.5 },
  planLimitNotice: { fontSize: '14px', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '5px', padding: '10px 12px', marginBottom: '15px', lineHeight: 1.5 },
  laneNotice: { fontSize: '14px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '5px', padding: '10px 12px', marginBottom: '15px' },
  // BLEEDING-STOP1: stale-snapshot warning shown above both footprint grids.
  staleWarning: { fontSize: '13px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', marginTop: '0', lineHeight: 1.5 },
  planCheckWarning: { fontSize: '13px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', marginTop: '0', lineHeight: 1.5 },
  laneNoticeTitle: { fontWeight: 600, marginBottom: '6px' },
  laneList: { margin: 0, paddingLeft: '18px' },
  laneItem: { marginBottom: '4px', lineHeight: 1.4 },
  success: { color: '#065F46', backgroundColor: '#D1FAE5', border: '1px solid #10B981', padding: '10px', borderRadius: '5px', marginBottom: '15px', fontSize: '14px' },
  error: { color: '#e74c3c', backgroundColor: '#fdeaea', padding: '10px', borderRadius: '5px', marginBottom: '15px', fontSize: '14px' },
  categoryGrid: { display: 'flex', flexWrap: 'wrap' as const, gap: '8px', marginBottom: '20px' },
  categoryChip: { padding: '8px 14px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' as const, transition: 'all 0.2s' },
  trustLoadingHint: { fontSize: '14px', color: '#7f8c8d', margin: '0' },
  trustHeaderBlock: { marginBottom: '18px' },
  trustEmptyBlock: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '18px' },
  trustEmptyHeading: { fontSize: '15px', fontWeight: 700, color: '#2c3e50', marginBottom: '4px' },
  trustEmptySub: { fontSize: '13px', color: '#7f8c8d', lineHeight: 1.5 },
  trustStats: { display: 'flex', flexWrap: 'wrap' as const, gap: '24px', marginBottom: '4px' },
  trustStatItem: { display: 'flex', flexDirection: 'column' as const, gap: '2px' },
  trustStatValue: { fontSize: '22px', fontWeight: 800, color: '#2c3e50', lineHeight: 1.1 },
  trustStatValueSm: { fontSize: '15px', fontWeight: 700, color: '#2c3e50', lineHeight: 1.3 },
  trustStatLabel: { fontSize: '12px', color: '#7f8c8d' },
  trustDisputeLine: { fontSize: '13px', color: '#b45309', marginTop: '14px', marginBottom: '0' },
  dangerSection: {
    backgroundColor: 'white', borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '12px', overflow: 'hidden',
    borderLeft: '4px solid #e74c3c',
  },
  dangerTitle: { fontSize: '16px', color: '#e74c3c', margin: '0' },
  buttonDanger: { padding: '12px 30px', backgroundColor: 'white', color: '#e74c3c', border: '2px solid #e74c3c', borderRadius: '5px', fontSize: '15px', cursor: 'pointer', fontWeight: '600' as const },
  buttonDangerFull: { padding: '12px 30px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', fontSize: '15px', cursor: 'pointer', fontWeight: '600' as const },
  buttonCancel: { padding: '12px 30px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '5px', fontSize: '15px', cursor: 'pointer', fontWeight: '600' as const },
  deleteButtons: { display: 'flex', gap: '10px', marginTop: '10px' },
};

export default Settings;